import React, { useState, useEffect, useCallback } from 'react';
import { 
  Banknote, Users, Check, X, RefreshCw, Search, ChevronDown,
  FileText, Calendar, Clock, User, Phone, Car, ArrowRight,
  DollarSign, AlertCircle, CheckCircle2, History
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const CashSettlementPage = () => {
  const [loading, setLoading] = useState(true);
  const [unsettledData, setUnsettledData] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverRides, setDriverRides] = useState([]);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [settlementForm, setSettlementForm] = useState({
    received_all: '',
    received_eur: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('unsettled');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [unsettledRes, settlementsRes] = await Promise.all([
        api.get('/cash/unsettled-totals'),
        api.get('/cash/settlements?limit=20')
      ]);
      setUnsettledData(unsettledRes.data);
      setSettlements(settlementsRes.data.settlements || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Gabim gjatë ngarkimit të të dhënave');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectDriver = async (driver) => {
    setSelectedDriver(driver);
    try {
      const res = await api.get(`/cash/driver/${driver.driver_id}/unsettled-rides`);
      setDriverRides(res.data.rides || []);
      setSettlementForm({
        received_all: driver.unsettled_all.toString(),
        received_eur: '',
        notes: ''
      });
      setShowSettlementModal(true);
    } catch (err) {
      toast.error('Gabim gjatë ngarkimit të udhëtimeve');
    }
  };

  const handleCreateSettlement = async (e) => {
    e.preventDefault();
    if (!selectedDriver) return;

    setSubmitting(true);
    try {
      const response = await api.post('/cash/settlements', {
        driver_id: selectedDriver.driver_id,
        received_all: parseFloat(settlementForm.received_all) || 0,
        received_eur: parseFloat(settlementForm.received_eur) || 0,
        notes: settlementForm.notes
      });
      
      toast.success(response.data.message);
      setShowSettlementModal(false);
      setSelectedDriver(null);
      setDriverRides([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gabim gjatë krijimit të mandatit');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (amount) => {
    return Number(amount || 0).toLocaleString('sq-AL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sq-AL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Banknote className="text-red-500" />
            Mandat Arkëtimi
          </h1>
          <p className="text-gray-500 mt-1">Menaxho dorëzimin e cash-it nga shoferët</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={18} />
          Rifresko
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Cash te Shoferët</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(unsettledData?.grand_total_all)} L</p>
              <p className="text-orange-200 text-sm mt-1">
                €{formatPrice(unsettledData?.grand_total_eur)} • {unsettledData?.total_rides || 0} udhëtime
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Banknote size={24} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Shoferë me Cash</p>
              <p className="text-3xl font-bold mt-1">{unsettledData?.drivers_count || 0}</p>
              <p className="text-blue-200 text-sm mt-1">
                {formatPrice(unsettledData?.grand_total_km)} km total
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users size={24} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Mandate Sot</p>
              <p className="text-3xl font-bold mt-1">
                {settlements.filter(s => new Date(s.created_at).toDateString() === new Date().toDateString()).length}
              </p>
              <p className="text-green-200 text-sm mt-1">Arkëtime të konfirmuara</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <CheckCircle2 size={24} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Kursi i Këmbimit</p>
              <p className="text-3xl font-bold mt-1">1€ = {unsettledData?.exchange_rate || 100} L</p>
              <p className="text-purple-200 text-sm mt-1">ALL / EUR</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <DollarSign size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('unsettled')}
          className={`px-4 py-2 font-medium border-b-2 transition ${
            activeTab === 'unsettled' 
              ? 'border-red-500 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <AlertCircle size={18} />
            Cash Pa Dorëzuar
            {unsettledData?.drivers_count > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                {unsettledData.drivers_count}
              </span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium border-b-2 transition ${
            activeTab === 'history' 
              ? 'border-red-500 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <History size={18} />
            Historiku i Mandateve
          </span>
        </button>
      </div>

      {/* Unsettled Cash Tab */}
      {activeTab === 'unsettled' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Shoferi</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Udhëtime</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Km</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cash (ALL)</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cash (EUR)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Veprim</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(!unsettledData?.drivers || unsettledData.drivers.length === 0) ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <p className="text-gray-500">Të gjithë shoferët kanë dorëzuar cash-in</p>
                  </td>
                </tr>
              ) : (
                unsettledData.drivers.map((driver) => (
                  <tr key={driver.driver_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={20} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{driver.driver_name}</p>
                          <p className="text-sm text-gray-500">{driver.driver_phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        <Car size={14} />
                        {driver.rides_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {formatPrice(driver.unsettled_km)} km
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-orange-600 text-lg">{formatPrice(driver.unsettled_all)} L</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      €{(driver.unsettled_all / (unsettledData?.exchange_rate || 100)).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleSelectDriver(driver)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 mx-auto"
                      >
                        <Banknote size={16} />
                        Arkëto
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nr. Mandati</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Data</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Shoferi</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Udhëtime</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Pritur</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Marrë</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Diferenca</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Dispatcher</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {settlements.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nuk ka mandate të regjistruara</p>
                  </td>
                </tr>
              ) : (
                settlements.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-900">{s.settlement_number}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(s.created_at)}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{s.driver_name}</p>
                        <p className="text-xs text-gray-500">{s.driver_phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {s.rides_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-700">
                      {formatPrice(s.expected_all)} L
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">
                      {formatPrice(s.received_all)} L
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${
                        s.difference_all === 0 ? 'text-gray-500' : 
                        s.difference_all > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {s.difference_all > 0 ? '+' : ''}{formatPrice(s.difference_all)} L
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s.dispatcher_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Settlement Modal */}
      {showSettlementModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Banknote size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Mandat Arkëtimi</h2>
                    <p className="text-red-100">Konfirmo dorëzimin e cash-it</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSettlementModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateSettlement} className="p-6 space-y-6">
              {/* Driver Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                    <User size={28} className="text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{selectedDriver.driver_name}</h3>
                    <p className="text-gray-500">{selectedDriver.driver_phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Udhëtime</p>
                    <p className="text-2xl font-bold text-blue-600">{selectedDriver.rides_count}</p>
                  </div>
                </div>
              </div>

              {/* Expected Amounts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-sm text-orange-600 font-medium">Cash i Pritur (ALL)</p>
                  <p className="text-3xl font-bold text-orange-700 mt-1">
                    {formatPrice(selectedDriver.unsettled_all)} L
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-600 font-medium">Ekuivalent (EUR)</p>
                  <p className="text-3xl font-bold text-blue-700 mt-1">
                    €{(selectedDriver.unsettled_all / (unsettledData?.exchange_rate || 100)).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Rides List */}
              {driverRides.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-600">Udhëtimet për dorëzim ({driverRides.length})</p>
                  </div>
                  <div className="max-h-48 overflow-auto">
                    {driverRides.slice(0, 10).map((ride, idx) => (
                      <div key={ride.id} className="px-4 py-2 border-b last:border-0 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">{idx + 1}.</span>
                          <span className="text-gray-700 truncate max-w-[200px]">{ride.dropoff_address || ride.pickup_address || 'Udhëtim'}</span>
                        </div>
                        <span className="font-medium text-gray-900">{formatPrice(ride.final_price || ride.estimated_price)} L</span>
                      </div>
                    ))}
                    {driverRides.length > 10 && (
                      <div className="px-4 py-2 text-center text-gray-500 text-sm">
                        + {driverRides.length - 10} udhëtime të tjera
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Received Amount Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shuma e Marrë Realisht (ALL) *
                  </label>
                  <input
                    type="number"
                    value={settlementForm.received_all}
                    onChange={(e) => setSettlementForm({...settlementForm, received_all: e.target.value})}
                    className="w-full px-4 py-3 border-2 rounded-xl text-lg font-bold focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder={selectedDriver.unsettled_all.toString()}
                    required
                  />
                  {parseFloat(settlementForm.received_all) !== selectedDriver.unsettled_all && settlementForm.received_all && (
                    <p className={`mt-2 text-sm font-medium ${
                      parseFloat(settlementForm.received_all) > selectedDriver.unsettled_all 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      Diferenca: {parseFloat(settlementForm.received_all) > selectedDriver.unsettled_all ? '+' : ''}
                      {formatPrice(parseFloat(settlementForm.received_all) - selectedDriver.unsettled_all)} L
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shënime (opsionale)
                  </label>
                  <textarea
                    value={settlementForm.notes}
                    onChange={(e) => setSettlementForm({...settlementForm, notes: e.target.value})}
                    className="w-full px-4 py-3 border rounded-xl"
                    rows={2}
                    placeholder="Shënime shtesë për mandatin..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowSettlementModal(false)}
                  className="flex-1 px-4 py-3 border-2 rounded-xl font-medium hover:bg-gray-50"
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  disabled={submitting || !settlementForm.received_all}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={20} />
                  )}
                  Konfirmo Mandatin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashSettlementPage;
